import { useRef, useState, type ComponentType, type KeyboardEvent } from 'react';
import {
  Bold,
  Code,
  Eye,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Pencil,
  Redo2,
  Undo2,
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Button } from './Button.js';
import { Textarea } from './Textarea.js';
import { cn } from '../../lib/utils.js';

marked.setOptions({ breaks: true, gfm: true });

/** Keystrokes within this window of the previous one are merged into the same undo step, so
 * undo doesn't require one press per character typed — only pauses (or a toolbar action) start
 * a new step. */
const HISTORY_COALESCE_MS = 600;

interface WrapAction {
  before: string;
  after?: string;
  placeholder?: string;
}

const TOOLBAR: Array<{ icon: ComponentType<{ className?: string }>; label: string; wrap: WrapAction }> = [
  { icon: Bold, label: 'Bold', wrap: { before: '**', after: '**', placeholder: 'bold text' } },
  { icon: Italic, label: 'Italic', wrap: { before: '_', after: '_', placeholder: 'italic text' } },
  { icon: Heading2, label: 'Heading', wrap: { before: '## ', placeholder: 'Heading' } },
  { icon: List, label: 'Bullet list', wrap: { before: '- ', placeholder: 'List item' } },
  { icon: ListOrdered, label: 'Numbered list', wrap: { before: '1. ', placeholder: 'List item' } },
  { icon: LinkIcon, label: 'Link', wrap: { before: '[', after: '](https://)', placeholder: 'link text' } },
  { icon: Code, label: 'Code', wrap: { before: '`', after: '`', placeholder: 'code' } },
];

export interface MarkdownEditorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  placeholder?: string;
}

/**
 * A minimal markdown editor: a formatting toolbar over a plain textarea, plus a sanitized
 * preview pane — used everywhere a PRD/TDD body used to be a raw <Textarea>. Deliberately not a
 * WYSIWYG editor; the stored value is still plain markdown text, so nothing about the PRD/TDD
 * schemas or the server needs to change.
 */
interface History {
  stack: string[];
  index: number;
}

export function MarkdownEditor({ id, value, onChange, rows = 6, className, placeholder }: MarkdownEditorProps) {
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastEditAtRef = useRef(0);

  // Its own undo/redo stack rather than the browser's native one: the toolbar buttons rewrite
  // `value` programmatically (not via a real keystroke/input event), which the browser's native
  // undo manager never sees, so Ctrl+Z would silently no-op right after using a toolbar button.
  const [history, setHistory] = useState<History>(() => ({ stack: [value], index: 0 }));
  // If `value` was changed by something other than this component (a different document loaded
  // into the same mounted editor, e.g. PrdPage re-seeding the form from a freshly-loaded
  // version), the current history no longer describes it — start over rather than let undo
  // silently do nothing or jump to unrelated content. Safe to do during render (not an effect):
  // it's a plain derived-state comparison, and it's a no-op once `history` catches up.
  if (value !== history.stack[history.index]) {
    setHistory({ stack: [value], index: 0 });
  }

  function record(next: string, coalesce: boolean) {
    const now = Date.now();
    const truncated = history.stack.slice(0, history.index + 1);
    if (coalesce && now - lastEditAtRef.current < HISTORY_COALESCE_MS && truncated.length - 1 === history.index) {
      truncated[truncated.length - 1] = next;
    } else {
      truncated.push(next);
    }
    lastEditAtRef.current = now;
    setHistory({ stack: truncated, index: truncated.length - 1 });
    onChange(next);
  }

  function undo() {
    if (history.index === 0) return;
    const nextIndex = history.index - 1;
    setHistory({ ...history, index: nextIndex });
    onChange(history.stack[nextIndex]!);
    lastEditAtRef.current = 0;
  }

  function redo() {
    if (history.index >= history.stack.length - 1) return;
    const nextIndex = history.index + 1;
    setHistory({ ...history, index: nextIndex });
    onChange(history.stack[nextIndex]!);
    lastEditAtRef.current = 0;
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(e.ctrlKey || e.metaKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      redo();
    }
  }

  function applyWrap({ before, after = '', placeholder: ph = '' }: WrapAction) {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || ph;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    record(next, false);
    const cursor = start + before.length + selected.length + after.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  }

  const canUndo = history.index > 0;
  const canRedo = history.index < history.stack.length - 1;

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-lg border border-border', className)}>
      <div className="flex items-center gap-0.5 border-b border-border bg-surface-2 p-1">
        <Button type="button" variant="ghost" size="icon" title="Undo (Ctrl+Z)" aria-label="Undo" disabled={!canUndo} onClick={undo}>
          <Undo2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title="Redo (Ctrl+Shift+Z)"
          aria-label="Redo"
          disabled={!canRedo}
          onClick={redo}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </Button>
        <div className="mx-1 h-5 w-px bg-border" />
        {TOOLBAR.map(({ icon: Icon, label, wrap }) => (
          <Button
            key={label}
            type="button"
            variant="ghost"
            size="icon"
            title={label}
            aria-label={label}
            disabled={mode === 'preview'}
            onClick={() => applyWrap(wrap)}
          >
            <Icon className="h-3.5 w-3.5" />
          </Button>
        ))}
        <div className="ml-auto flex gap-0.5">
          <Button
            type="button"
            variant={mode === 'write' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setMode('write')}
          >
            <Pencil className="h-3.5 w-3.5" /> Write
          </Button>
          <Button
            type="button"
            variant={mode === 'preview' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setMode('preview')}
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </Button>
        </div>
      </div>

      {mode === 'write' ? (
        <Textarea
          ref={textareaRef}
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => record(e.target.value, true)}
          onKeyDown={handleKeyDown}
          className="max-w-none rounded-none border-none"
        />
      ) : (
        <div
          className="doc-prose max-w-none overflow-auto p-3 text-sm"
          style={{ minHeight: `${rows * 1.5}rem` }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(marked.parse(value || '_Nothing to preview_', { async: false })),
          }}
        />
      )}
    </div>
  );
}
